/**
 * Outil « Des intérêts composés à e » (Boîte à outils, 1re → Tle).
 *
 * « Placement » : 1 € à 100 % par an, intérêts versés n fois au taux 1/n ;
 * au bout d'un an uₙ = (1 + 1/n)ⁿ. Plus on verse souvent, plus on gagne,
 * mais uₙ plafonne vers e ≈ 2,718 : les décimales justes s'allument une à
 * une (courbe en échelle logarithmique). « eᵏˣ » : la pente de la tangente
 * est k fois la hauteur, f′ = k f ; pour k = 1, pente 1 en 0. Calculs :
 * `compound.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn, sampleFunctionPath } from '@jeraisonne/ui';
import type { ToolProps } from '../../tool-types';
import { Note, ParamSlider, Segmented } from '../../ui/controls';
import { makeFrame } from '../../ui/frame';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText, PlotGrid } from '../../ui/svg-parts';
import {
  compound,
  correctDecimals,
  doublingTime,
  E_DIGITS,
  exactText,
  expAt,
  nOfSlider,
  STEPS,
  truncText,
} from './compound';

type Mode = 'savings' | 'exp';

const W = 320;
const H = 200;
const PAD_L = 34;
const PAD_R = 12;
const Y_MIN = 1.9;
const Y_MAX = 2.85;
const gx = (t: number) => PAD_L + (t / 6) * (W - PAD_L - PAD_R);
const gy = (v: number) => H - 26 - ((v - Y_MIN) / (Y_MAX - Y_MIN)) * (H - 40);
const CURVE = 'var(--color-primary-500)';
const E_COLOR = 'var(--color-accent-700)';
/** coefficient k en LaTeX devant x : « », « - », « 0{,}5 » */
const kCoef = (k: number) =>
  k === 1 ? '' : k === -1 ? '-' : formatNumber(k, 1).replace('−', '-').replace(',', '{,}');

/** « f(x) = e^{0,5x}, f′(x) = 0,5 e^{0,5x} = 0,5 f(x) » (cas k = 0, ±1 écrits simplement) */
function expTex(k: number): string {
  if (k === 0) return "f(x) = e^{0} = 1, \\quad f'(x) = 0";
  const ex = `e^{${kCoef(k)}x}`;
  const c = kCoef(k);
  const lead = c === '' ? '' : c === '-' ? '-' : `${c}\\,`;
  return `f(x) = ${ex}, \\quad f'(x) = ${lead}${ex} = ${lead}f(x)`;
}

/** 525600 → « 525 600 » (espace fine insécable U+202F) */
const groupInt = (n: number) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202F');

const SIZE = 320;
const X_MIN = -3;
const X_MAX = 3;

export function CompoundETool({ level }: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  // ln n'arrive qu'en Terminale
  const lnKnown = rank === null || rank >= 7;
  const [mode, setMode] = useState<Mode>('savings');
  const [t, setT] = useState(Math.log10(12));
  const [k, setK] = useState(1);
  const [x0, setX0] = useState(0);

  const n = nOfSlider(t);
  const u = compound(n);
  const ok = correctDecimals(u);
  const exactU = exactText(n);
  const text = exactU ?? truncText(u, 9);
  const decimalsText =
    ok === 0
      ? 'aucune décimale juste'
      : `${ok} décimale${ok > 1 ? 's' : ''} juste${ok > 1 ? 's' : ''}`;
  // « 2, » + les décimales justes, en évidence
  const good = text.slice(0, ok > 0 ? 2 + ok : 1);
  const rest = text.slice(good.length);

  const curve = Array.from({ length: 121 }, (_, i) => {
    const tt = (i / 120) * 6;
    return `${i ? 'L' : 'M'}${gx(tt).toFixed(1)},${gy(compound(nOfSlider(tt))).toFixed(1)}`;
  }).join(' ');

  const pt = expAt(k, x0);
  const dbl = doublingTime(Math.abs(k));
  const tanY = (x: number) => pt.y + pt.slope * (x - x0);
  // fenêtre qui suit le point : il reste visible même quand e^(kx₀) dépasse 50
  const yMax = Math.max(6, 1.25 * pt.y);
  const vp = { xMin: X_MIN, xMax: X_MAX, yMin: -0.1 * yMax, yMax };
  const expFrame = makeFrame(vp, SIZE);

  return (
    <div className="space-y-4">
      <Segmented
        ariaLabel="Mode"
        value={mode}
        options={[
          { value: 'savings', label: 'Placement' },
          { value: 'exp', label: 'Exponentielle' },
        ]}
        onChange={setMode}
      />

      {mode === 'savings' ? (
        <>
          <p className="text-ink-700 text-sm">
            1 € placé à 100 % par an. Si les intérêts sont versés n fois dans l’année, chaque fois
            au taux 1/n, on a au bout d’un an :
          </p>
          <ParamSlider
            name="n (versements par an)"
            ariaLabel="Nombre de versements par an"
            value={t}
            min={0}
            max={6}
            step={0.05}
            format={() => groupInt(n)}
            onChange={setT}
          />
          <div className="flex flex-wrap gap-1" role="group" aria-label="Exemples">
            {STEPS.map((s) => (
              <button
                key={s.n}
                type="button"
                aria-pressed={n === s.n}
                onClick={() => setT(Math.log10(s.n))}
                className={cn(
                  'min-h-11 rounded-xl border px-2 text-sm',
                  n === s.n ? 'border-primary-500 bg-primary-50 font-semibold' : 'border-ink-200',
                )}
              >
                {s.label}
              </button>
            ))}
          </div>

          <section
            className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
            data-testid="ce-value"
          >
            <MathText className="text-ink-900 block text-lg">
              {`$u_n = \\left(1 + \\frac{1}{n}\\right)^n$`}
            </MathText>
            <p className="font-display text-2xl tabular-nums" data-testid="ce-u">
              <span className="text-ink-600 text-base">uₙ = </span>
              <span className="text-success-600 font-bold">{good}</span>
              <span className="text-ink-700">
                {rest}
                {exactU === null ? '…' : ''}
              </span>
            </p>
            <p className="text-ink-600 text-sm">
              e = {E_DIGITS}… : {decimalsText} pour n = {groupInt(n)}. Intérêts gagnés :{' '}
              {formatNumber(u - 1, 4)} €.
            </p>
            <p className="sr-only" aria-live="polite">
              Pour n = {groupInt(n)}, uₙ {exactU === null ? `≈ ${truncText(u, 6)}` : `= ${exactU}`}{' '}
              : {decimalsText}.
            </p>
          </section>

          <div className="mx-auto w-full max-w-[26rem]">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className={cn('border-ink-100 block w-full rounded-2xl border', FIGURE_BG_CLASS)}
              role="img"
              aria-label="uₙ en fonction de n (échelle logarithmique) : la suite monte vers e sans le dépasser."
              data-testid="ce-graph"
            >
              {[2, 2.25, 2.5, 2.75].map((v) => (
                <g key={v}>
                  <line
                    x1={PAD_L}
                    x2={W - PAD_R}
                    y1={gy(v)}
                    y2={gy(v)}
                    stroke="var(--color-ink-100)"
                  />
                  <text
                    x={PAD_L - 4}
                    y={gy(v) + 4}
                    textAnchor="end"
                    fontSize={12}
                    fill="var(--color-ink-500)"
                  >
                    {String(v).replace('.', ',')}
                  </text>
                </g>
              ))}
              {[0, 1, 2, 3, 4, 5, 6].map((p) => (
                <text
                  key={p}
                  x={gx(p)}
                  y={H - 8}
                  textAnchor="middle"
                  fontSize={12}
                  fill="var(--color-ink-500)"
                >
                  {p === 0 ? '1' : `10${'⁰¹²³⁴⁵⁶'[p]}`}
                </text>
              ))}
              <line
                x1={PAD_L}
                x2={W - PAD_R}
                y1={gy(Math.E)}
                y2={gy(Math.E)}
                stroke={E_COLOR}
                strokeDasharray="6 4"
                strokeWidth={1.5}
              />
              <HaloText
                x={W - PAD_R - 4}
                y={gy(Math.E) - 6}
                textAnchor="end"
                fontSize={13}
                fontWeight={700}
                fill={E_COLOR}
              >
                e
              </HaloText>
              <path d={curve} fill="none" stroke={CURVE} strokeWidth={2.5} />
              <circle cx={gx(t)} cy={gy(u)} r={6} fill={CURVE} />
            </svg>
          </div>
          <Note live={false}>
            Verser plus souvent rapporte plus, mais de moins en moins : uₙ croît et reste sous e.
            Même versés à chaque seconde, les intérêts ne dépassent pas e − 1 ≈ 1,72 €. C’est l’une
            des façons de définir e : la limite de (1 + 1/n)ⁿ.
          </Note>
        </>
      ) : (
        <>
          <ParamSlider
            name={<MathText>{'$k$'}</MathText>}
            ariaLabel="k"
            value={k}
            min={-2}
            max={2}
            step={0.1}
            format={(v) => formatNumber(v, 1).replace('-', '−')}
            onChange={setK}
          />
          <ParamSlider
            name={<MathText>{'$x_0$'}</MathText>}
            ariaLabel="x0"
            value={x0}
            min={-2}
            max={2}
            step={0.1}
            format={(v) => formatNumber(v, 1).replace('-', '−')}
            onChange={setX0}
          />
          <div className="mx-auto w-full max-w-[26rem]">
            <svg
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className={cn(
                'border-ink-100 block aspect-square w-full rounded-2xl border',
                FIGURE_BG_CLASS,
              )}
              role="img"
              aria-label={`Courbe de e puissance ${formatNumber(k, 1)} x et sa tangente au point d’abscisse ${formatNumber(x0, 1)}.`}
              data-testid="ce-exp"
            >
              <PlotGrid frame={expFrame} />
              <path
                d={sampleFunctionPath((x) => Math.exp(k * x), [X_MIN, X_MAX], expFrame)}
                fill="none"
                stroke={CURVE}
                strokeWidth={3}
              />
              <line
                x1={expFrame.tx(X_MIN)}
                y1={expFrame.ty(tanY(X_MIN))}
                x2={expFrame.tx(X_MAX)}
                y2={expFrame.ty(tanY(X_MAX))}
                stroke={E_COLOR}
                strokeWidth={2}
              />
              <circle
                cx={expFrame.tx(x0)}
                cy={expFrame.ty(pt.y)}
                r={6}
                fill="var(--color-ink-900)"
              />
            </svg>
          </div>
          <section
            className="border-ink-100 space-y-1 rounded-2xl border px-3 py-3"
            data-testid="ce-slope"
          >
            <MathText className="text-ink-900 block overflow-x-auto">{`$${expTex(k)}$`}</MathText>
            <p className="text-ink-700 text-sm">
              En x₀ = {formatNumber(x0, 1)} : hauteur f(x₀) ≈ {formatNumber(pt.y, 3)}, pente f′(x₀)
              ≈ {formatNumber(pt.slope, 3)}, soit {formatNumber(k, 1)} fois la hauteur.
            </p>
            <p className="sr-only" aria-live="polite">
              k = {formatNumber(k, 1)}, x₀ = {formatNumber(x0, 1)} : pente ≈{' '}
              {formatNumber(pt.slope, 3)}, hauteur ≈ {formatNumber(pt.y, 3)}.
            </p>
            {k === 1 && x0 === 0 && (
              <p className="text-success-600 text-sm font-semibold">
                k = 1 : en 0, la tangente a pour pente 1. C’est ce qui caractérise eˣ parmi les
                fonctions x ↦ aˣ.
              </p>
            )}
            {k !== 0 && dbl !== null && (
              <p className="text-ink-600 text-sm">
                {k > 0 ? 'f double' : 'f est divisée par 2'} chaque fois que x augmente{' '}
                {lnKnown
                  ? `de ln 2${Math.abs(k) === 1 ? '' : ` / ${formatNumber(Math.abs(k), 1)}`} ≈ `
                  : 'd’environ '}
                {formatNumber(dbl, 2)} : {k > 0 ? 'croissance' : 'décroissance'} exponentielle.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
